import _ from 'lodash'
import React, { Fragment } from 'react'
import { graph } from '@adc/constants'
import { G } from '@adc/sdk'
import Modal from '../../../overlays/modal'
import Text from '../../../elementals/text'
import { BADGE_ORDER_PRIORITY, BADGE_MODE } from './base'
import FullWidthBadge from './fullWidthBadge'
import { getAssetType, isREO } from './utils'

const occupancyStatus = listing =>
  _.get(listing, 'listing_configuration.occupancy_status', '')

class PropertyOccupiedBadge extends FullWidthBadge {
  static title = 'Occupancy'

  static titleIcon = 'users'

  static textIcon = 'times'

  static summary = 'Occupied'

  static renderPriority = BADGE_ORDER_PRIORITY.SECOND

  static contentPositive = false

  static shouldBeHoisted = true

  static shouldBeIncluded(listing, badgeMode) {
    // only applies to expanded, hide for condensed
    return (
      badgeMode === BADGE_MODE.EXPANDED &&
      occupancyStatus(listing) === graph.enums.OccupancyStatus.OCCUPIED
    )
  }

  get content() {
    const zip = _.get(
      this.props.listing,
      'primary_property.summary.address.postal_code',
      null
    )

    const refLink = zip
      ? `/residential/${zip}_zp/active_lt/vacant_os/`
      : `/residential/active_lt/vacant_os/`

    const refMessage = zip
      ? 'No thanks, show me vacant properties in this area'
      : 'No thanks, show me vacant properties'

    // the first headerText variable covers the logic to show a different occupancy acknowledgement for bank owned once the copy is finalized in ticket: BUY-714
    // there is a util for isREO which looks at whether an event is a trustee_sale or not but the product requirement for
    // this text change wants it only to be for REO and Day1 REO product types, not rentals or short sales which could also be trustee_sale: false
    const headerText =
      getAssetType(this.props.listing) === G.AssetType.BankOwned
        ? 'This property may be occupied and the buyer will be responsible for obtaining possession after closing.  Do not disturb occupants or trespass.'
        : 'Do not disturb occupants. Trespassing is a criminal offense.'

    return (
      <>
        {headerText}{' '}
        <Modal
          title='Why bid on occupied properties?'
          size='tiny'
          mobileTitleSkin='light'
          fullScreen='mobileOnly'
          elmId='occupancy_modal'
          trigger={
            <a data-elm-id='occupancy_modal_trigger'>
              Learn more about occupied properties.
            </a>
          }
        >
          <Modal.Content data-elm-id='occupancy_modal_content'>
            <Text>
              Although the idea of buying an occupied property may seem
              intimidating, some investors see opportunity because they can:
              <ul>
                <li>Buy the property before it hits the MLS</li>
                <li>Turn an occupant into a paying tenant</li>
              </ul>
              Upon closing, you are responsible for any evictions. If you've
              never done this, be sure to research your local laws.
              <ul>
                <li>
                  <a href='/blog/buying-an-occupied-property/' target='_blank'>
                    Learn More
                  </a>
                </li>
                <li>
                  <a href={refLink} target='_blank' rel='noreferrer'>
                    {refMessage}
                  </a>
                </li>
              </ul>
            </Text>
          </Modal.Content>
        </Modal>
      </>
    )
  }
}

class PropertyUnknownOccupancyBadge extends PropertyOccupiedBadge {
  static summary = 'Occupancy Unknown'

  static shouldBeIncluded(listing, badgeMode) {
    // only applies to expanded, hide for condensed
    const isUnknown =
      occupancyStatus(listing) === graph.enums.OccupancyStatus.UNKNOWN ||
      _.isNil(occupancyStatus(listing))
    return badgeMode === BADGE_MODE.EXPANDED && isUnknown
  }
}

class PropertyVacantBadge extends PropertyOccupiedBadge {
  static title = 'Occupancy'

  static titleIcon = 'users'

  static textIcon = 'check'

  static summary = 'Vacant'

  static condensedTitle = 'Vacant'

  static condensedTitleIcon = 'sign'

  static renderPriority = BADGE_ORDER_PRIORITY.SECOND

  static contentPositive = true

  static shouldBeHoisted = true

  static shouldBeIncluded(listing) {
    return occupancyStatus(listing) === graph.enums.OccupancyStatus.VACANT
  }

  get content() {
    return isREO(this.props.listing)
      ? 'Although the property is vacant, do not enter unless interior access is available.'
      : 'Although the property is believed to be vacant, do not enter premises. Trespassing is a crime.'
  }
}

export {
  PropertyOccupiedBadge,
  PropertyVacantBadge,
  PropertyUnknownOccupancyBadge,
}
